import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTheme } from "../../../contexts/ThemeContext";
import {
    useGetPaymentDetailsQuery,
    useDeleteSubscriptionPaymentMutation,
    useSetDefaultPaymentMethodMutation,
} from "../../../services";
import { adminRoutes } from "../../../routers/AdminRouters";
import { useAppSelector } from "../../../redux/hooks";
import { RootState } from "../../../redux/store";
import { selectOrganisationId } from "../../../redux/organisationReducer";
import ConfirmActionModal from "../licenses/screenModal/ConfirmActionModal";

const PaymentDetailsList: React.FC = () => {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const isDark = theme === "dark";
    const organisationId = useAppSelector(selectOrganisationId);

    const { isLoggedIn } = useAppSelector((state: RootState) => state.admin);

    // Fetch Payment Methods
    const { data: paymentResp, isLoading } = useGetPaymentDetailsQuery({
        organisationId: organisationId as string,
    });

    const [deletePayment] = useDeleteSubscriptionPaymentMutation();
    const [setDefault] = useSetDefaultPaymentMethodMutation();

    // State for Confirmation Modals
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isDefaultModalOpen, setIsDefaultModalOpen] = useState(false);
    const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(null);

    const openDeleteModal = (id: string) => {
        setSelectedPaymentId(id);
        setIsDeleteModalOpen(true);
    };

    const openDefaultModal = (id: string) => {
        setSelectedPaymentId(id);
        setIsDefaultModalOpen(true);
    };

    const closeModals = () => {
        setIsDeleteModalOpen(false);
        setIsDefaultModalOpen(false);
        setSelectedPaymentId(null);
    };

    const handleDelete = async () => {
        if (selectedPaymentId) {
            try {
                await deletePayment(selectedPaymentId).unwrap();
                closeModals();
            } catch (err) {
                // Error handled by RTK Query's onQueryStarted
            }
        }
    };

    const handleSetDefault = async () => {
        if (selectedPaymentId) {
            try {
                await setDefault(selectedPaymentId).unwrap();
                closeModals();
            } catch (err) {
                // Error handled by RTK Query's onQueryStarted
            }
        }
    };

    const paymentMethods = paymentResp?.results || [];

    // State for expanded card
    // Default to the first card or the actual default card if available
    const [expandedId, setExpandedId] = useState<string | null>(null);

    const toggleExpand = (id: string) => {
        setExpandedId(expandedId === id ? null : id);
    };

    // Sidebar width logic (consistent with other pages)
    type NavbarSliceState = { isOpen: boolean };
    const isSidebarOpen = useAppSelector(
        (state: RootState) => (state.navbar as NavbarSliceState).isOpen,
    );
    const sidebarWidth = isSidebarOpen ? 256 : 64;

    const getCardIcon = (brand: string) => {
        const lowerBrand = brand?.toLowerCase();
        if (lowerBrand === "visa") {
            return (
                <svg
                    width="32"
                    height="20"
                    viewBox="0 0 32 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <rect width="32" height="20" rx="4" fill="#1A1F71" />
                    <path
                        d="M11.9723 13.9114L13.8407 5.64551H16.8291L14.9621 13.9114H11.9723ZM21.9082 5.80164C21.3283 5.57861 20.3952 5.37891 19.349 5.37891C16.5916 5.37891 14.6508 6.75 14.6366 8.71875C14.621 10.1797 16.0354 10.9936 17.0913 11.4792C18.176 11.9772 18.5413 12.2961 18.5359 12.748C18.5289 13.4375 17.6536 13.7461 16.8841 13.7461C15.6987 13.7461 14.9961 13.4323 14.4442 13.2031L13.9219 15.5417C14.5977 15.8346 15.8454 16.0755 17.1325 16.0885C20.0487 16.0885 21.9392 14.7435 21.9548 12.6458C21.9688 10.8958 20.8354 10.1615 19.3094 19.4896 18.3848 9.07422 17.8437 8.70573 17.8437 8.13021C17.8437 7.625 18.4375 7.08724 19.7041 7.08724C20.803 7.06771 21.7259 7.29167 22.3333 7.53906L22.8659 5.29557C21.9082 5.06641 21.3283 5.16146 21.9082 5.80164ZM26.9601 5.64551H24.3164C23.5182 5.64551 22.9557 5.86458 22.6016 6.64583L18.151 16.1406H21.2858L21.9102 14.5429H25.7331L26.0964 16.1406H28.8464L26.5601 5.64551H26.9601ZM22.7812 12.388L24.5755 7.84375L25.6016 12.388H22.7812ZM9.08333 5.64551L6.15104 12.7305L5.83333 11.2318C5.28906 9.53906 3.79427 7.53906 2.05729 6.6849L2 6.65755L4.72656 16.1406H7.8776L12.5599 5.64551H9.08333Z"
                        fill="white"
                    />
                </svg>
            );
        }
        // Mastercard as default if not visa
        return (
            <div className="flex -space-x-1.5">
                <div className="w-5 h-5 rounded-full bg-[#EB001B]"></div>
                <div className="w-5 h-5 rounded-full bg-[#F79E1B]"></div>
            </div>
        );
    };

    return (
        <div
            className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen ${isDark ? "bg-[#0B0B0B]" : "bg-gray-50"
                }`}
            style={{
                marginLeft: sidebarWidth,
                width: `calc(100% - ${sidebarWidth}px)`,
                transition: "margin-left 0.3s ease, width 0.3s ease",
            }}
        >
            {/* Header / Breadcrumb */}
            <div className="mb-6">
                <h1
                    className={`text-xl font-bold mb-2 ${isDark ? "text-gray-100" : "text-gray-900"
                        }`}
                >
                    Subscription Overview
                </h1>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Link to="/" className="hover:text-[#B6CB24] transition-colors">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                            <polyline points="9 22 9 12 15 12 15 22"></polyline>
                        </svg>
                    </Link>
                    <span>/</span>
                    <Link to="/" className="hover:text-[#B6CB24] transition-colors">
                        Dashboard
                    </Link>
                    <span>/</span>
                    <span className="text-[#B6CB24] font-medium">Payment Details</span>
                </div>
            </div>

            {/* Main Content Area */}
            <div
                className={`rounded-xl border p-6 ${isDark ? "bg-[#111111] border-[#1F1F1F]" : "bg-white border-gray-200"
                    }`}
            >
                <div className="flex items-center justify-between mb-6">
                    <h2
                        className={`text-base font-semibold ${isDark ? "text-white" : "text-gray-900"
                            }`}
                    >
                        Payment Methods
                    </h2>
                    <button
                        onClick={() => navigate("/payment-details/create")}
                        className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-[#B6CB24] border border-[#B6CB24] rounded-lg hover:bg-[#B6CB24]/10 transition-colors"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="8" x2="12" y2="16"></line>
                            <line x1="8" y1="12" x2="16" y2="12"></line>
                        </svg>
                        Add new payment
                    </button>
                </div>

                {/* List of Payment Methods */}
                <div className="space-y-4">
                    {isLoading ? (
                        <div
                            className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}
                        >
                            Loading...
                        </div>
                    ) : paymentMethods.length === 0 ? (
                        <div
                            className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}
                        >
                            No payment methods found.
                        </div>
                    ) : (
                        paymentMethods.map((method: any, index: number) => {
                            const isExpanded =
                                expandedId === method._id ||
                                (index === 0 && expandedId === null);
                            const isDefault = method.default;
                            const currentlyExpanded = isExpanded;

                            return (
                                <div
                                    key={method._id}
                                    className={`rounded-lg border transition-all duration-200 ${isDark
                                        ? "bg-[#0F0F0F] border-[#2C2C2C]"
                                        : "bg-gray-50 border-gray-200"
                                        }`}
                                >
                                    <div className="p-4">
                                        {/* Header Row: Number + Default Badge + Actions + Chevron */}
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-6">
                                                <div className="flex flex-col">
                                                    <span className="text-[10px] text-gray-500 mb-0.5">
                                                        Card Number
                                                    </span>
                                                    <div className="flex items-center gap-3">
                                                        <span
                                                            className={`text-sm font-semibold tracking-wider ${isDark ? "text-white" : "text-gray-900"}`}
                                                        >
                                                            •••• •••• •••• {method.card?.last4 || "****"}
                                                        </span>
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            width="27"
                                                            height="17"
                                                            viewBox="0 0 27 17"
                                                            fill="none"
                                                        >
                                                            <path
                                                                d="M17.1505 1.78348H9.84961V14.9034H17.1505V1.78348Z"
                                                                fill="#FF5F00"
                                                            />
                                                            <path
                                                                d="M10.3133 8.34388C10.3121 7.08034 10.5985 5.8331 11.1506 4.69658C11.7027 3.56005 12.5062 2.56403 13.5002 1.78393C12.2693 0.816414 10.791 0.214734 9.23432 0.0476536C7.67764 -0.119426 6.10536 0.154837 4.69718 0.839095C3.289 1.52335 2.10174 2.58999 1.27112 3.91711C0.440489 5.24422 0 6.77826 0 8.34388C0 9.90951 0.440489 11.4436 1.27112 12.7707C2.10174 14.0978 3.289 15.1644 4.69718 15.8487C6.10536 16.5329 7.67764 16.8072 9.23432 16.6401C10.791 16.473 12.2693 15.8714 13.5002 14.9038C12.5062 14.1237 11.7027 13.1277 11.1506 11.9912C10.5985 10.8547 10.3121 9.60742 10.3133 8.34388V8.34388Z"
                                                                fill="#EB001B"
                                                            />
                                                            <path
                                                                d="M26.9997 8.34388C26.9998 9.90948 26.5594 11.4435 25.7288 12.7706C24.8982 14.0977 23.711 15.1644 22.3028 15.8487C20.8947 16.5329 19.3224 16.8072 17.7658 16.6401C16.2091 16.473 14.7309 15.8714 13.5 14.9038C14.4931 14.123 15.2959 13.1268 15.848 11.9904C16.4 10.8541 16.6868 9.60722 16.6868 8.34388C16.6868 7.08054 16.4 5.83369 15.848 4.69734C15.2959 3.561 14.4931 2.56482 13.5 1.78392C14.7309 0.816411 16.2091 0.21473 17.7658 0.0476521C19.3224 -0.119426 20.8947 0.154842 22.3028 0.839107C23.711 1.52337 24.8982 2.59002 25.7288 3.91714C26.5594 5.24425 26.9998 6.77828 26.9997 8.34388V8.34388Z"
                                                                fill="#F79E1B"
                                                            />
                                                            <path
                                                                d="M26.2046 13.514V13.2454H26.3129V13.1907H26.0371V13.2454H26.1455V13.514H26.2046ZM26.7402 13.514V13.1901H26.6556L26.5584 13.4129L26.4611 13.1901H26.3765V13.514H26.4362V13.2697L26.5274 13.4803H26.5893L26.6805 13.2692V13.514H26.7402Z"
                                                                fill="#F79E1B"
                                                            />
                                                        </svg>
                                                        <div className="ml-3">
                                                            {isDefault ? (
                                                                <div className="px-5 py-1.5 rounded-full text-[11px] font-semibold bg-[#2B2E15] text-[#C6D92C] border border-[#3A3F1F]">
                                                                    Default
                                                                </div>
                                                            ) : (
                                                                <button
                                                                    onClick={() => openDefaultModal(method._id)}
                                                                    className={`px-5 py-1.5 text-[11px] font-semibold rounded-full border transition-colors ${isDark
                                                                        ? "bg-[#262626] border-[#333] text-white hover:bg-[#333]"
                                                                        : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                                                                        }`}
                                                                >
                                                                    Set as Default
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-4">
                                                {/* Edit Action */}
                                                <button
                                                    onClick={() => navigate(`/payment-details/edit/${method._id}`)}
                                                    className="text-[#B6CB24] hover:bg-[#B6CB24]/10 transition-colors p-2 bg-[#1A1A1A] rounded-lg border border-[#2C2C2C]"
                                                >
                                                    <svg
                                                        width="18"
                                                        height="18"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    >
                                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                                    </svg>
                                                </button>

                                                {/* Delete Action - Only show if not default */}
                                                {!method.default && (
                                                    <button
                                                        onClick={() => openDeleteModal(method._id)}
                                                        className="text-red-500 hover:bg-red-500/10 transition-colors p-2 bg-[#1A1A1A] rounded-lg border border-[#2C2C2C]"
                                                    >
                                                        <svg
                                                            width="18"
                                                            height="18"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            strokeWidth="2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        >
                                                            <polyline points="3 6 5 6 21 6"></polyline>
                                                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                            <line x1="10" y1="11" x2="10" y2="17"></line>
                                                            <line x1="14" y1="11" x2="14" y2="17"></line>
                                                        </svg>
                                                    </button>
                                                )}

                                                {/* Collapse/Expand Chevron */}
                                                <button
                                                    onClick={() => toggleExpand(method._id)}
                                                    className={`text-gray-400 hover:text-white transition-all duration-200 p-1 ${currentlyExpanded ? "rotate-180" : ""}`}
                                                >
                                                    <svg
                                                        width="20"
                                                        height="20"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2.5"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    >
                                                        <polyline points="6 9 12 15 18 9"></polyline>
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>

                                        {/* Expanded Details */}
                                        {currentlyExpanded && (
                                            <div
                                                className={`mt-6 pt-6 border-t ${isDark ? "border-[#2C2C2C]" : "border-gray-200"}`}
                                            >
                                                <div className="grid grid-cols-4 gap-y-6 gap-x-4">
                                                    {/* Name */}
                                                    <div>
                                                        <div className="text-[10px] text-gray-400 mb-1">
                                                            Name on Payment Method
                                                        </div>
                                                        <div
                                                            className={`text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}
                                                        >
                                                            {method.billingDetails?.name || "N/A"}
                                                        </div>
                                                    </div>

                                                    {/* Expiry */}
                                                    <div>
                                                        <div className="text-[10px] text-gray-400 mb-1">
                                                            Expiry Date
                                                        </div>
                                                        <div
                                                            className={`text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}
                                                        >
                                                            {method.card?.expiryMonth}/
                                                            {method.card?.expiryYear?.toString().slice(-2)}
                                                        </div>
                                                    </div>

                                                    {/* CVC Check */}
                                                    <div>
                                                        <div className="text-[10px] text-gray-400 mb-1">
                                                            CVC check
                                                        </div>
                                                        <div
                                                            className={`text-sm font-bold ${isDark ? "text-white" : "text-gray-900"}`}
                                                        >
                                                            {method.card?.cvcChecked ? "Passed" : "Passed"}{" "}
                                                            {/* Assuming passed for now based on design */}
                                                        </div>
                                                    </div>

                                                    {/* Billing Address */}
                                                    <div>
                                                        <div className="text-[10px] text-gray-400 mb-1">
                                                            Billing Address
                                                        </div>
                                                        <div
                                                            className={`text-sm font-medium ${isDark ? "text-white" : "text-gray-900"} leading-relaxed`}
                                                        >
                                                            {method.billingDetails?.address || "N/A"}
                                                        </div>
                                                    </div>

                                                    {/* Country */}
                                                    <div>
                                                        <div className="text-[10px] text-gray-400 mb-1">
                                                            Country
                                                        </div>
                                                        <div
                                                            className={`text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}
                                                        >
                                                            {method.billingDetails?.country || "N/A"}
                                                        </div>
                                                    </div>

                                                    {/* State */}
                                                    <div>
                                                        <div className="text-[10px] text-gray-400 mb-1">
                                                            State
                                                        </div>
                                                        <div
                                                            className={`text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}
                                                        >
                                                            {method.billingDetails?.state || "N/A"}
                                                        </div>
                                                    </div>

                                                    {/* City */}
                                                    <div>
                                                        <div className="text-[10px] text-gray-400 mb-1">
                                                            City
                                                        </div>
                                                        <div
                                                            className={`text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}
                                                        >
                                                            {method.billingDetails?.city || "N/A"}
                                                        </div>
                                                    </div>

                                                    {/* Zip */}
                                                    <div>
                                                        <div className="text-[10px] text-gray-400 mb-1">
                                                            Postal / Zip code
                                                        </div>
                                                        <div
                                                            className={`text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}
                                                        >
                                                            {method.billingDetails?.postal || "N/A"}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
            <ConfirmActionModal
                isOpen={isDeleteModalOpen}
                onClose={closeModals}
                onConfirm={handleDelete}
                title="Delete Payment Method"
                message="Are you sure you want to delete this payment method? This action cannot be undone."
                confirmText="Delete"
                cancelText="Cancel"
                type="delete"
            />
            <ConfirmActionModal
                isOpen={isDefaultModalOpen}
                onClose={closeModals}
                onConfirm={handleSetDefault}
                title="Set Default Payment Method"
                message="Are you sure you want to set this as your default payment method?"
                confirmText="Set Default"
                cancelText="Cancel"
                type="action"
            />
        </div>
    );
};

export default PaymentDetailsList;
